/**
 * Blur annotations in the editor: hide part of the screen (emails, passwords) by pixelating or
 * blurring what is drawn below. Drawn with the editor's shared drawing, so the preview and the
 * export match.
 *
 * Boxes are stored like other annotations (shares of the picture, as seen without zoom). During
 * a zoom they stay on the same part of the video, so what they hide never shows.
 */
import { videoBox, type VideoFrameStyle } from './videoFrame';
import { zoomViewport, type ZoomView } from './videoZoom';

export type BlurStyle = 'pixelate' | 'blur';
/** Block size in display pixels. */
export const BLUR_STRENGTH = { min: 6, max: 40, default: 16 };

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * How picture positions (shares 0 – 1) move at a zoom: `toScreen` where a stored point shows,
 * `toPicture` the stored point for a screen position. Null without a zoom (they are the same).
 */
export function zoomMapping(W: number, H: number, f: VideoFrameStyle, zoom: ZoomView | null) {
  if (!zoom || !W || !H) return null;
  const b = videoBox(W, H, f);
  const v = zoomViewport(zoom);
  return {
    /** The video's box in display units: a zoomed blur is kept inside it. */
    box: b,
    toScreen: (x: number, y: number) => ({
      x: (b.x + (((x * W - b.x) / b.w - v.left) / v.width) * b.w) / W,
      y: (b.y + (((y * H - b.y) / b.h - v.top) / v.height) * b.h) / H,
    }),
    toPicture: (x: number, y: number) => ({
      x: (b.x + (v.left + ((x * W - b.x) / b.w) * v.width) * b.w) / W,
      y: (b.y + (v.top + ((y * H - b.y) / b.h) * v.height) * b.h) / H,
    }),
  };
}

export type ZoomMapping = ReturnType<typeof zoomMapping>;

/** Where a blur box (stored shares) shows, in display units (positive size; clipped while zoomed). */
export function blurBoxOnScreen(a: Box, W: number, H: number, map: ZoomMapping): Box {
  let x0 = Math.min(a.x, a.x + a.w);
  let y0 = Math.min(a.y, a.y + a.h);
  let x1 = Math.max(a.x, a.x + a.w);
  let y1 = Math.max(a.y, a.y + a.h);
  if (map) {
    const p0 = map.toScreen(x0, y0);
    const p1 = map.toScreen(x1, y1);
    const b = map.box;
    x0 = Math.max(p0.x * W, b.x) / W;
    y0 = Math.max(p0.y * H, b.y) / H;
    x1 = Math.min(p1.x * W, b.x + b.w) / W;
    y1 = Math.min(p1.y * H, b.y + b.h) / H;
  }
  return { x: x0 * W, y: y0 * H, w: Math.max(0, (x1 - x0) * W), h: Math.max(0, (y1 - y0) * H) };
}

let small: HTMLCanvasElement | null = null;

/**
 * Pixelates (or blurs) what is already drawn in the box (display units; the context's transform
 * maps them to pixels): the area is drawn small, then scaled back up.
 */
export function drawBlur(ctx: CanvasRenderingContext2D, box: Box, style: BlurStyle, strength: number) {
  const m = ctx.getTransform();
  const x0 = Math.max(0, Math.floor(m.a * box.x + m.e));
  const y0 = Math.max(0, Math.floor(m.d * box.y + m.f));
  const x1 = Math.min(ctx.canvas.width, Math.ceil(m.a * (box.x + box.w) + m.e));
  const y1 = Math.min(ctx.canvas.height, Math.ceil(m.d * (box.y + box.h) + m.f));
  if (x1 - x0 < 1 || y1 - y0 < 1) return;
  // An invalid stored strength must not break the draw loop.
  const block = Math.max(2, (Number.isFinite(strength) ? strength : BLUR_STRENGTH.default) * m.a);
  small ??= document.createElement('canvas');
  small.width = Math.max(1, Math.round((x1 - x0) / block));
  small.height = Math.max(1, Math.round((y1 - y0) / block));
  const sctx = small.getContext('2d');
  if (!sctx) return;
  sctx.imageSmoothingEnabled = true;
  sctx.drawImage(ctx.canvas, x0, y0, x1 - x0, y1 - y0, 0, 0, small.width, small.height);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = style === 'blur';
  ctx.drawImage(small, 0, 0, small.width, small.height, x0, y0, x1 - x0, y1 - y0);
  ctx.restore();
}
